import { GeometryType } from '../../../../geometry/GeometryType';
import { buildTopologyEdgeRef, buildTopologyFaceRef, buildTopologyVertexRef } from '../../../../topology/TopologyReferenceBuilders';
import { resolveEdgeReferencePoints } from '../../../../utils/feature/topologyEdgeReferencePoints';
import { sampleEdgeCurveFromPuppyKernel } from '../../../../utils/feature/topologyCurveSurfaceAnalysis';
import { isPuppyFaceReversed } from '../../../../utils/feature/puppyFaceOrientation';
import type { GeometryState } from '../../../engine/state';
import {
    resolveFirstExistingWorkerModelBodyRevisionId,
    resolveWorkerModelBodyRevisionId,
} from '../../../model-body/resolveWorkerModelBodyRevisionId';
import {
    arePuppyShapesSame,
    deduplicatePuppyShapes,
} from '../../../../utils/feature/puppyShapeIdentity';
import type { BlendEdgeTopologyCandidate } from './matcher/BlendEdgeTopologyMatcher';
import { BlendEdgeTopologyMatcher } from './matcher/BlendEdgeTopologyMatcher';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;
type ShapeKind = 'edge' | 'face' | 'vertex';

type ShapeWithDelete = {
    delete?: () => void;
};

type ShapeIdentity = Record<string, unknown> & {
    IsSame?: (other: unknown) => boolean;
    IsEqual?: (other: unknown) => boolean;
    IsPartner?: (other: unknown) => boolean;
    HashCode?: (upperBound: number) => number;
};

type TopologyPoint3D = [number, number, number];

/**
 * 面几何签名。
 *
 * 设计说明：签名取自曲面参数域中点及其单位法向，是“点@法向”格式面引用的
 * 计算来源；blend 下游（provenance 构建与面引用回解）必须共用同一套计算口径。
 */
export type FaceDomainSignature = {
    /** 曲面参数域中点。 */
    point: TopologyPoint3D;
    /** 参数域中点处的单位法向。 */
    normal: TopologyPoint3D;
};

type BlendSourceSolidObject = GeometryObject & {
    type: GeometryType.SOLID;
    shape?: unknown;
};

export interface BlendFeatureBuildInput<TDefinition> {
    puppy: PuppyKernel;
    state: GeometryState;
    featureId: string;
    upstreamBodyObjectId?: string;
    definition: TDefinition;
}

export interface BlendFeatureBuildResult {
    shape: unknown;
    sourceObjectId: string;
    topologyProvenance: SerializedTopologyProvenanceMap;
    topologyMap: Record<string, string>;
    previewShape?: unknown | null;
    blendGuides?: BlendPreviewGuide[];
    partialPreviewApplied?: boolean;
    partialPreviewTargetRef?: string | null;
}

export interface ResolvedBlendSource {
    sourceObjectId: string;
    sourceObject: BlendSourceSolidObject;
}

export interface ResolvedTopologySelection {
    edgeShape: unknown;
    selection: SelectionItemRef;
}

export interface ResolveTopologySelectionOptions {
    sourceObjectId?: string;
}

const blendEdgeTopologyMatcher = new BlendEdgeTopologyMatcher();

const TOPOLOGY_REF_PARSE_TOLERANCE = 1e-6;
const EDGE_PERSISTENT_REF_MATCH_TOLERANCE = 1e-4;
const TOPOLOGY_SHAPE_HASH_UPPER_BOUND = 2147483647;

function safeDelete(value: unknown): void {
    const deleteMethod = (value as ShapeWithDelete | null)?.delete;
    if (typeof deleteMethod !== 'function') {
        return;
    }
    try {
        deleteMethod.call(value);
    } catch {
        // Puppy CAD 内核 清理失败不应覆盖真实求解错误。
    }
}

/**
 * 解析面的几何签名（参数域中点 + 单位法向）。
 *
 * 设计说明：
 * 1. 抽壳/倒角/圆角下游会把用户拾取到的面编码成“点@法向”格式的稳定引用；
 * 2. 当 provenance 无法按 ref 精确命中（例如 blend 结果使用轻量索引 ref）时，
 *    解析方必须能用当前 Shape 重新计算签名并回绑真实面；
 * 3. 这里与 buildTopologyFaceRef 的量化口径保持一致，签名可直接编码成稳定引用字符串比较。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape 待签名的面 Shape。
 * @returns 面几何签名；无法解析时返回 null。
 */
export function resolveFaceSignature(
    puppy: PuppyKernel,
    faceShape: unknown,
): FaceDomainSignature | null {
    let surface: unknown = null;
    let point: unknown = null;
    let tangentU: unknown = null;
    let tangentV: unknown = null;

    try {
        surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true);
        const surfaceValue = surface as {
            FirstUParameter: () => number;
            LastUParameter: () => number;
            FirstVParameter: () => number;
            LastVParameter: () => number;
            D1: (u: number, v: number, pointArg: unknown, d1u: unknown, d1v: unknown) => void;
        };
        const u = (surfaceValue.FirstUParameter() + surfaceValue.LastUParameter()) / 2;
        const v = (surfaceValue.FirstVParameter() + surfaceValue.LastVParameter()) / 2;
        point = new puppy.gp_Pnt_3(0, 0, 0);
        tangentU = new puppy.gp_Vec_4(0, 0, 0);
        tangentV = new puppy.gp_Vec_4(0, 0, 0);
        surfaceValue.D1(u, v, point, tangentU, tangentV);

        const pointValue = point as { X: () => number; Y: () => number; Z: () => number };
        const tangentUValue = tangentU as { X: () => number; Y: () => number; Z: () => number };
        const tangentVValue = tangentV as { X: () => number; Y: () => number; Z: () => number };
        const normalX = tangentUValue.Y() * tangentVValue.Z() - tangentUValue.Z() * tangentVValue.Y();
        const normalY = tangentUValue.Z() * tangentVValue.X() - tangentUValue.X() * tangentVValue.Z();
        const normalZ = tangentUValue.X() * tangentVValue.Y() - tangentUValue.Y() * tangentVValue.X();
        const normalLength = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);
        /**
         * 参数曲面切向叉乘尚未包含 Puppy CAD 内核 面的拓扑方向。
         * 背面标记为 TopAbs_REVERSED 时必须整体翻转，保证签名法向是实体外法向，
         * 与权威拓扑快照（buildAuthorityTopologySnapshot）和网格化方向保持一致。
         */
        const orientationScale = isPuppyFaceReversed(puppy, faceShape) ? -1 : 1;
        const resolvedNormal: [number, number, number] = normalLength > Number.EPSILON
            ? [
                normalX === 0 ? 0 : (normalX / normalLength) * orientationScale,
                normalY === 0 ? 0 : (normalY / normalLength) * orientationScale,
                normalZ === 0 ? 0 : (normalZ / normalLength) * orientationScale,
            ]
            : [0, 0, orientationScale];
        return {
            point: [pointValue.X(), pointValue.Y(), pointValue.Z()],
            normal: resolvedNormal,
        };
    } catch {
        return null;
    } finally {
        safeDelete(surface);
        safeDelete(point);
        safeDelete(tangentU);
        safeDelete(tangentV);
    }
}

function resolveShapeHash(shape: unknown): number | undefined {
    const hashCode = (shape as ShapeIdentity | null)?.HashCode;
    if (typeof hashCode !== 'function') {
        return undefined;
    }
    try {
        const result = hashCode.call(shape, TOPOLOGY_SHAPE_HASH_UPPER_BOUND);
        return Number.isInteger(result) ? result : undefined;
    } catch {
        return undefined;
    }
}

/**
 * 判断两个 Puppy CAD 内核 Shape 是否是拓扑上的"同一项"。
 * 只使用 IsSame（比较 TShape + Location + Orientation）；
 * 禁止使用 IsPartner——它只比较底层 TShape 指针，
 * 对拉伸体(BRepPrimAPI_MakePrism)会把底面边与顶面边错误合并
 * （两者共享同一 TShape 但 location 不同），导致权威快照丢失一半拓扑项。
 */
export const areShapesEquivalent = arePuppyShapesSame;

/**
 * 按 Puppy CAD 内核 拓扑等价关系对 Shape 集合去重。
 *
 * 设计说明：
 * 1. TopExp_Explorer 在 Solid 上枚举边/点时，同一物理拓扑可能因相邻面被重复返回；
 * 2. 顶点倒角这类 solver 需要判断"物理相邻边"数量，不能直接消费重复枚举结果；
 * 3. 去重逻辑统一放在底层拓扑工具中，避免各 solver 自行补丁式过滤。
 *
 * @param shapes 原始 Shape 集合。
 * @returns 保持首次出现顺序的去重 Shape 集合。
 */
export const dedupeEquivalentShapes = deduplicatePuppyShapes;

export function enumerateShapeItems(
    puppy: PuppyKernel,
    shape: unknown,
    kind: ShapeKind,
): unknown[] {
    if (!shape) {
        return [];
    }

    const explorerCtor = (puppy as Record<string, unknown>).TopExp_Explorer_2 as
        | (new (shapeArg: unknown, kindArg: unknown, stopKindArg: unknown) => {
            More: () => boolean;
            Current: () => unknown;
            Next: () => void;
            delete?: () => void;
        })
        | undefined;
    const topAbs = (puppy as Record<string, unknown>).TopAbs_ShapeEnum as Record<string, unknown> | undefined;
    const topoDs = (puppy as Record<string, unknown>).TopoDS as Record<string, ((shapeArg: unknown) => unknown)> | undefined;
    if (!explorerCtor || !topAbs) {
        return [];
    }

    const kindToken = kind === 'face'
        ? topAbs.TopAbs_FACE
        : kind === 'edge'
            ? topAbs.TopAbs_EDGE
            : topAbs.TopAbs_VERTEX;
    const wrapperName = kind === 'face' ? 'Face' : kind === 'edge' ? 'Edge' : 'Vertex';
    const wrapper = topoDs?.[wrapperName];
    const explorer = new explorerCtor(shape, kindToken, topAbs.TopAbs_SHAPE);
    const items: unknown[] = [];

    try {
        while (explorer.More()) {
            const current = explorer.Current();
            items.push(typeof wrapper === 'function' ? wrapper(current) : current);
            explorer.Next();
        }
    } finally {
        safeDelete(explorer);
    }

    return items;
}

export function extractVertexPoint(
    puppy: PuppyKernel,
    vertexShape: unknown,
): [number, number, number] | null {
    const pointGetter = ((puppy as Record<string, unknown>).BRep_Tool as Record<string, unknown> | undefined)?.Pnt as
        | ((vertex: unknown) => { X: () => number; Y: () => number; Z: () => number })
        | undefined;
    if (!pointGetter) {
        return null;
    }
    const point = pointGetter.call((puppy as Record<string, unknown>).BRep_Tool, vertexShape);
    return [point.X(), point.Y(), point.Z()];
}

/**
 * 按 Puppy CAD 内核 原始枚举顺序提取边端点。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边 Shape。
 * @returns 原始顺序端点。
 */
function extractEdgeVerticesInExplorerOrder(puppy: PuppyKernel, edgeShape: unknown): Array<[number, number, number]> {
    return enumerateShapeItems(puppy, edgeShape, 'vertex')
        .slice(0, 2)
        .map((vertex) => extractVertexPoint(puppy, vertex))
        .filter((point): point is [number, number, number] => Array.isArray(point));
}

/**
 * 按 PuppyCAD 稳定拓扑引用顺序排列边端点。
 *
 * @param vertices 原始端点。
 * @returns 与 itemRef 字符串端点顺序一致的端点。
 */
function orderEdgeVerticesByTopologyRef(
    vertices: Array<[number, number, number]>,
): Array<[number, number, number]> {
    if (vertices.length !== 2) {
        return vertices;
    }
    return [...vertices].sort((left, right) => (
        buildTopologyVertexRef(left).localeCompare(buildTopologyVertexRef(right))
    ));
}

/**
 * 按 PuppyCAD 拓扑引用顺序提取边端点。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边 Shape。
 * @returns 与 itemRef 首尾一致的边端点。
 */
export function extractEdgeVertices(puppy: PuppyKernel, edgeShape: unknown): Array<[number, number, number]> {
    const vertices = extractEdgeVerticesInExplorerOrder(puppy, edgeShape);
    if (vertices.length !== 2) {
        return vertices;
    }
    /**
     * Puppy CAD 内核 的 TopExp_Explorer 返回边端点时受 TopoDS_Edge 方向影响，并不保证与
     * PuppyCAD 的持久化 edge ref 顺序一致。变量圆角、部分边线和后续其它特征
     * 都会把 parameter=0/1 解释为 itemRef 字符串中的首/尾端点，因此 worker
     * 对外暴露边端点时必须统一按拓扑引用顺序排序，不能把 Puppy CAD 内核 原始顺序外泄。
     */
    return orderEdgeVerticesByTopologyRef(vertices);
}

/**
 * 按几何端点引用对边 Shape 去重。
 *
 * 设计说明：
 * 1. 同一物理边在不同面内可能带有不同方向，单纯 IsSame 可能把方向差异视为不同 Shape；
 * 2. 顶点倒角需要的是物理相邻边数量，因此这里优先使用排序后的端点引用作为身份；
 * 3. 无法解析端点时回退到 Puppy CAD 内核 Shape 等价去重，保证异常拓扑仍可被后续 solver 明确诊断。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShapes 原始边 Shape 集合。
 * @returns 去重后的物理边 Shape 集合。
 */
export function dedupeEdgeShapesByGeometry(
    puppy: PuppyKernel,
    edgeShapes: unknown[],
): unknown[] {
    const seenGeometryRefs = new Set<string>();
    const unresolvedEdges: unknown[] = [];
    const resolvedEdges = edgeShapes.filter((edgeShape) => {
        const vertices = extractEdgeVertices(puppy, edgeShape);
        if (vertices.length < 2) {
            unresolvedEdges.push(edgeShape);
            return false;
        }
        const geometryRef = buildEdgeGeometryRefFromShape(puppy, edgeShape) ?? buildTopologyEdgeRef(vertices[0]!, vertices[1]!);
        if (seenGeometryRefs.has(geometryRef)) {
            return false;
        }
        seenGeometryRefs.add(geometryRef);
        return true;
    });
    return [
        ...resolvedEdges,
        ...dedupeEquivalentShapes(unresolvedEdges),
    ];
}

/**
 * 判断 Puppy CAD 内核 原始边方向是否已经与 PuppyCAD 拓扑引用顺序一致。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边 Shape。
 * @returns 原始方向是否与拓扑引用方向一致。
 */
export function isEdgeVertexOrderAlignedWithTopologyRef(puppy: PuppyKernel, edgeShape: unknown): boolean {
    const rawVertices = extractEdgeVerticesInExplorerOrder(puppy, edgeShape);
    if (rawVertices.length !== 2) {
        return true;
    }
    const orderedVertices = orderEdgeVerticesByTopologyRef(rawVertices);
    return arePointsWithinTolerance(rawVertices[0]!, orderedVertices[0]!)
        && arePointsWithinTolerance(rawVertices[1]!, orderedVertices[1]!);
}

/**
 * 渲染侧 edge ref 来自 Float32 网格坐标，和 Puppy CAD 内核 双精度端点做 1e-6 字符串比较会出现“同一条边 ref 不相等”。
 * 这里把 ref 反解成几何点，再用容差匹配 Puppy CAD 内核 端点，确保命令执行以真实拓扑为准。
 */
function parseSerializedTopologyPoint(
    serializedPoint: string,
    tolerance: number = TOPOLOGY_REF_PARSE_TOLERANCE,
): TopologyPoint3D | null {
    const coordinates = serializedPoint
        .split(',')
        .map((value) => Number.parseInt(value, 10));
    if (coordinates.length !== 3 || coordinates.some((value) => !Number.isFinite(value))) {
        return null;
    }
    return [
        coordinates[0]! * tolerance,
        coordinates[1]! * tolerance,
        coordinates[2]! * tolerance,
    ];
}

function parseTopologyEdgeRef(
    persistentRef: string,
    tolerance: number = TOPOLOGY_REF_PARSE_TOLERANCE,
): [TopologyPoint3D, TopologyPoint3D] | null {
    const parts = persistentRef.split('|');
    if (parts.length !== 2) {
        return null;
    }
    const pointA = parseSerializedTopologyPoint(parts[0]!, tolerance);
    const pointB = parseSerializedTopologyPoint(parts[1]!, tolerance);
    if (!pointA || !pointB) {
        return null;
    }
    return [pointA, pointB];
}

function arePointsWithinTolerance(
    left: TopologyPoint3D,
    right: TopologyPoint3D,
    tolerance: number = EDGE_PERSISTENT_REF_MATCH_TOLERANCE,
): boolean {
    return Math.abs(left[0] - right[0]) <= tolerance
        && Math.abs(left[1] - right[1]) <= tolerance
        && Math.abs(left[2] - right[2]) <= tolerance;
}

function matchEdgeVerticesByGeometry(
    persistentRef: string,
    vertices: TopologyPoint3D[],
): boolean {
    if (vertices.length < 2) {
        return false;
    }
    const refVertices = parseTopologyEdgeRef(persistentRef);
    if (!refVertices) {
        return false;
    }
    const [refPointA, refPointB] = refVertices;
    const actualPointA = vertices[0]!;
    const actualPointB = vertices[1]!;
    return (
        (arePointsWithinTolerance(refPointA, actualPointA) && arePointsWithinTolerance(refPointB, actualPointB))
        || (arePointsWithinTolerance(refPointA, actualPointB) && arePointsWithinTolerance(refPointB, actualPointA))
    );
}

function resolveTopologyProvenance(metadata: Record<string, unknown>): SerializedTopologyProvenanceMap | null {
    const candidate = metadata.topologyProvenance as SerializedTopologyProvenanceMap | undefined;
    if (!candidate || !Array.isArray(candidate.edges) || !Array.isArray(candidate.faces)) {
        return null;
    }
    return candidate;
}

/**
 * 解析候选边对应的权威出处记录。
 *
 * @param provenance 当前实体的拓扑出处表。
 * @param geometryPersistentRef 按当前 Puppy CAD 内核 边端点生成的几何引用。
 * @param authorityTopologyIndex 与 viewer 权威快照一致的去重拓扑索引。
 * @param rawEdgeIndex 原始 Puppy CAD 内核 枚举索引，用于兼容历史未去重 provenance。
 * @returns 命中的边出处记录及身份强度；没有命中时记录与强度均为空。
 */
function resolveEdgeProvenanceEntry(
    provenance: SerializedTopologyProvenanceMap | null,
    geometryPersistentRef: string | null,
    authorityTopologyIndex: number,
    rawEdgeIndex: number,
): {
    entry: SerializedTopologyProvenanceMap['edges'][number] | null;
    identityStrength: BlendEdgeTopologyCandidate['provenanceIdentityStrength'];
} {
    if (!provenance) {
        return {
            entry: null,
            identityStrength: null,
        };
    }
    if (geometryPersistentRef) {
        const matchedByRef = provenance.edges.find((entry) => entry.ref === geometryPersistentRef);
        if (matchedByRef) {
            return {
                entry: matchedByRef,
                identityStrength: 'strong',
            };
        }
    }
    const matchedByAuthorityIndex = provenance.edges.find((entry) => (
        entry.edgeIndex === authorityTopologyIndex
    ));
    if (matchedByAuthorityIndex) {
        return {
            entry: matchedByAuthorityIndex,
            identityStrength: 'weak',
        };
    }
    const matchedByRawIndex = provenance.edges.find((entry) => (
        entry.edgeIndex === rawEdgeIndex
    )) ?? null;
    return {
        entry: matchedByRawIndex,
        identityStrength: matchedByRawIndex ? 'weak' : null,
    };
}

function buildEdgeTopologyCandidates(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    edges: unknown[],
): BlendEdgeTopologyCandidate[] {
    const provenance = resolveTopologyProvenance(metadata);
    const faces = enumerateShapeItems(puppy, shape, 'face');

    /**
     * 对候选列表按物理边去重，只保留每条物理边首次出现的候选。
     *
     * 设计说明：
     * 1. TopExp_Explorer 枚举 Solid 边时，同一条物理边可能出现多次（被多个 Face 共享）；
     * 2. 如果不去重，matcher 会因相同 persistentRef 的多个候选触发歧义错误；
     * 3. 这里只跳过后续重复出现，保持首次出现的 edgeIndex 不变，
     *    确保返回的 edgeIndex 可正确索引到 edges 数组中的 edgeShape。
     */
    const seenEdgeIndexes = new Set<number>();
    return edges.reduce<BlendEdgeTopologyCandidate[]>((result, edgeShape, edgeIndex) => {
        /** 跳过物理上等价的重复边（IsSame），只保留首次出现 */
        const firstOccurrence = edges.findIndex((candidate) => areShapesEquivalent(candidate, edgeShape));
        if (firstOccurrence !== edgeIndex) {
            return result;
        }
        if (seenEdgeIndexes.has(edgeIndex)) {
            return result;
        }
        seenEdgeIndexes.add(edgeIndex);
        const authorityTopologyIndex = result.length;
        const vertices = extractEdgeVertices(puppy, edgeShape);
        const geometryPersistentRef = buildEdgeGeometryRefFromShape(puppy, edgeShape);
        const provenanceResolution = resolveEdgeProvenanceEntry(
            provenance,
            geometryPersistentRef,
            authorityTopologyIndex,
            edgeIndex,
        );
        const provenanceEdge = provenanceResolution.entry;
        /**
         * 候选边需要保留 provenance 回绑的可信度，禁止把索引猜测伪装成权威身份。
         *
         * 设计说明：
         * 1. viewer 选择事件携带的是 `subEntityId / persistentRef` 这类权威身份；
         * 2. 当前几何 ref 命中出处表时，证明身份与实际 Puppy CAD 内核 边一致，可以作为强身份；
         * 3. 几何 ref 未命中时仍保留索引回绑用于兼容，但它只能是弱身份，后续匹配器必须让当前几何证据优先。
         */
        const adjacentFaceRefs = faces.reduce<string[]>((faceRefs, faceShape, faceIndex) => {
            const faceEdges = enumerateShapeItems(puppy, faceShape, 'edge');
            if (!faceEdges.some((candidate) => areShapesEquivalent(candidate, edgeShape))) {
                return faceRefs;
            }
            const faceRef = provenance?.faces.find((entry) => entry.faceIndex === faceIndex)?.ref
                ?? buildTopologyFaceRef(faceIndex);
            return [...faceRefs, faceRef];
        }, []);

        return [
            ...result,
            {
                edgeIndex,
                // 语义身份只能来自出处表；几何 ref 未命中出处时不得沿用索引回绑的语义名。
                semanticEdgeName: provenanceResolution.identityStrength === 'strong'
                    ? provenanceEdge?.semanticName ?? null
                    : null,
                subEntityId: provenanceEdge?.subEntityId ?? null,
                provenanceIdentityStrength: provenanceResolution.identityStrength,
                topologyIndex: authorityTopologyIndex,
                geometryPersistentRef,
                provenancePersistentRef: provenanceEdge?.ref ?? null,
                adjacentFaceRefs,
                shapeHash: resolveShapeHash(edgeShape),
            },
        ];
    }, []);
}

function resolveEdgeIndicesByAdjacentFaces(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    edges: unknown[],
    selection: SelectionItemRef,
): number[] {
    const faceRefs = (selection.adjacentFaceRefs ?? []).filter((faceRef): faceRef is string => faceRef.length > 0);
    if (faceRefs.length === 0) {
        return [];
    }

    try {
        /**
         * 当同一条渲染边因几何量化误差命中多条 Puppy CAD 内核 候选边时，优先用选择时携带的相邻面引用做交集。
         * 这是比端点容差更强的拓扑语义，可以把“看起来端点一样”的 seam/重复边收敛到真实共享边。
         */
        const sharedEdges = faceRefs.reduce<unknown[] | null>((accumulator, faceRef, index) => {
            const faceShape = resolveFaceShapeByRef(puppy, shape, metadata, faceRef);
            const faceEdges = enumerateShapeItems(puppy, faceShape, 'edge');
            if (index === 0) {
                return faceEdges;
            }
            return (accumulator ?? []).filter((candidate) => faceEdges.some((faceEdge) => areShapesEquivalent(faceEdge, candidate)));
        }, null) ?? [];

        return edges.reduce<number[]>((matched, edgeShape, edgeIndex) => {
            if (sharedEdges.some((candidate) => areShapesEquivalent(candidate, edgeShape))) {
                matched.push(edgeIndex);
            }
            return matched;
        }, []);
    } catch {
        return [];
    }
}

function resolveFaceIndexByRuntimeHint(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    faceHint: RuntimeTopologyFaceHint,
    faces: unknown[],
): number | null {
    const hintedFaceShapeHash = faceHint.shapeHash;
    if (Number.isInteger(faceHint.shapeHash)) {
        const hashedIndices = faces.reduce<number[]>((matched, faceShape, faceIndex) => {
            if (resolveShapeHash(faceShape) === hintedFaceShapeHash) {
                matched.push(faceIndex);
            }
            return matched;
        }, []);
        if (hashedIndices.length === 1) {
            return hashedIndices[0]!;
        }
        if (hashedIndices.includes(faceHint.topologyIndex)) {
            return faceHint.topologyIndex;
        }
    }

    if (Number.isInteger(faceHint.topologyIndex) && faceHint.topologyIndex >= 0 && faceHint.topologyIndex < faces.length) {
        const hintedFace = faces[faceHint.topologyIndex];
        if (hintedFace) {
            const provenance = resolveTopologyProvenance(metadata);
            const hintedRef = provenance?.faces.find((entry) => entry.faceIndex === faceHint.topologyIndex)?.ref;
            if (hintedRef === faceHint.persistentRef || buildTopologyFaceRef(faceHint.topologyIndex) === faceHint.persistentRef) {
                return faceHint.topologyIndex;
            }
        }
    }

    try {
        return resolveFaceIndexByPersistentRef(puppy, faces, metadata, faceHint.persistentRef);
    } catch {
        return null;
    }
}

function resolveEdgeIndicesByAdjacentFaceHints(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    edges: unknown[],
    faceHints: RuntimeTopologyFaceHint[],
): number[] {
    if (faceHints.length === 0) {
        return [];
    }

    const faces = enumerateShapeItems(puppy, shape, 'face');
    const resolvedFaceIndices = faceHints
        .map((faceHint) => resolveFaceIndexByRuntimeHint(puppy, shape, metadata, faceHint, faces))
        .filter((faceIndex): faceIndex is number => Number.isInteger(faceIndex));
    if (resolvedFaceIndices.length !== faceHints.length) {
        return [];
    }

    const uniqueFaceRefs = resolvedFaceIndices
        .map((faceIndex) => {
            const provenance = resolveTopologyProvenance(metadata);
            return provenance?.faces.find((entry) => entry.faceIndex === faceIndex)?.ref ?? buildTopologyFaceRef(faceIndex);
        })
        .filter((faceRef): faceRef is string => typeof faceRef === 'string' && faceRef.length > 0);

    return resolveEdgeIndicesByAdjacentFaces(
        puppy,
        shape,
        metadata,
        edges,
        {
            kind: 'edge',
            bodyObjectId: '',
            persistentRef: '',
            adjacentFaceRefs: uniqueFaceRefs,
        },
    );
}

/**
 * 解析纯索引格式的面引用（face:<index>）。
 *
 * 设计说明：
 * 1. 只有“face:”后跟纯非负整数才是索引引用；
 * 2. 必须先用正则收口再 parseInt，避免把畸形后缀截断成伪索引；
 * 3. 越界直接抛错，与 resolveFaceShapeByRef 原有的“面索引越界”语义保持一致。
 *
 * @param faces 当前源体面集合。
 * @param persistentRef 面索引引用。
 * @returns 面索引。
 */
function resolveFaceIndexByIndexRef(faces: unknown[], persistentRef: string): number {
    const rawIndex = persistentRef.slice('face:'.length);
    if (!/^\d+$/.test(rawIndex)) {
        throw new Error(`未找到面拓扑引用: ${persistentRef}`);
    }
    const parsed = Number.parseInt(rawIndex, 10);
    if (parsed >= 0 && parsed < faces.length) {
        return parsed;
    }
    throw new Error(`面索引越界，无法解析面引用: ${persistentRef}`);
}

/**
 * 按几何签名把“点@法向”格式面引用回绑到当前源体的面。
 *
 * 设计说明：
 * 1. blend 结果 provenance 使用轻量索引 ref，用户拾取到的却是几何签名 ref，
 *    必须用当前 Shape 重新计算面签名完成回绑；
 * 2. 签名按 buildTopologyFaceRef 的同一量化口径编码后做字符串精确比较；
 * 3. 命中多个同签名面时抛歧义，不能把坐标量化值当索引静默错绑；
 * 4. 抽壳移除面等下游统一走 resolveFaceShapeByRef，因此该回绑只在
 *    provenance 精确命中失败时执行，不影响稳定策略数据的既有命中路径。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faces 当前源体面集合。
 * @param persistentRef 面几何签名引用。
 * @returns 唯一命中的面索引。
 */
function resolveFaceIndexBySignatureRef(
    puppy: PuppyKernel,
    faces: unknown[],
    persistentRef: string,
): number {
    const matchedIndices: number[] = [];
    faces.forEach((faceShape, faceIndex) => {
        const signature = resolveFaceSignature(puppy, faceShape);
        if (signature && buildTopologyFaceRef(signature) === persistentRef) {
            matchedIndices.push(faceIndex);
        }
    });
    if (matchedIndices.length === 1) {
        return matchedIndices[0]!;
    }
    if (matchedIndices.length > 1) {
        throw new Error(`面拓扑引用匹配存在歧义: ${persistentRef}`);
    }
    throw new Error(`未找到面拓扑引用: ${persistentRef}`);
}

function resolveFaceIndexByPersistentRef(
    puppy: PuppyKernel,
    faces: unknown[],
    metadata: Record<string, unknown>,
    persistentRef: string,
): number {
    const provenance = resolveTopologyProvenance(metadata);
    const indexedByRef = provenance?.faces.find((entry) => entry.ref === persistentRef)?.faceIndex;
    if (typeof indexedByRef === 'number') {
        return indexedByRef;
    }
    if (persistentRef.startsWith('face:')) {
        // “face:点@法向”是几何签名引用，“face:纯数字”是索引引用，
        // 两种格式必须分流解析；旧实现对签名做 parseInt 会把坐标量化值截断成伪索引。
        if (persistentRef.slice('face:'.length).includes('@')) {
            return resolveFaceIndexBySignatureRef(puppy, faces, persistentRef);
        }
        return resolveFaceIndexByIndexRef(faces, persistentRef);
    }
    throw new Error(`未找到面拓扑引用: ${persistentRef}`);
}

export function resolveFaceShapesByEdge(
    puppy: PuppyKernel,
    shape: unknown,
    edgeShape: unknown,
): unknown[] {
    const faces = enumerateShapeItems(puppy, shape, 'face');
    return faces.filter((faceShape) => {
        const faceEdges = enumerateShapeItems(puppy, faceShape, 'edge');
        return faceEdges.some((candidate) => areShapesEquivalent(candidate, edgeShape));
    });
}

export function resolveSharedEdgeShapes(
    puppy: PuppyKernel,
    leftShape: unknown,
    rightShape: unknown,
): unknown[] {
    const leftEdges = enumerateShapeItems(puppy, leftShape, 'edge');
    const rightEdges = enumerateShapeItems(puppy, rightShape, 'edge');
    return leftEdges.filter((leftEdge) => rightEdges.some((rightEdge) => areShapesEquivalent(leftEdge, rightEdge)));
}

function resolveVertexIndexByPersistentRef(
    puppy: PuppyKernel,
    shape: unknown,
    persistentRef: string,
): number {
    const vertices = enumerateShapeItems(puppy, shape, 'vertex');
    const fallbackIndex = vertices.findIndex((vertexShape) => {
        const point = extractVertexPoint(puppy, vertexShape);
        if (!point) {
            return false;
        }
        return buildTopologyVertexRef(point) === persistentRef;
    });
    if (fallbackIndex >= 0) {
        return fallbackIndex;
    }
    throw new Error(`未找到顶点拓扑引用: ${persistentRef}`);
}

function resolveVertexIndexByRuntimeTopologyHint(
    pointRef: BlendPointRef,
    vertices: unknown[],
): number | null {
    const runtimeHint = pointRef.runtimeTopologyHint;
    if (!runtimeHint || runtimeHint.kind !== 'vertex' || !Number.isInteger(runtimeHint.topologyIndex)) {
        return null;
    }
    if (runtimeHint.sourceObjectId !== pointRef.bodyObjectId) {
        return null;
    }
    const hintedIndex = runtimeHint.topologyIndex;
    return hintedIndex >= 0 && hintedIndex < vertices.length ? hintedIndex : null;
}

function resolveVertexIndexByPoint(
    puppy: PuppyKernel,
    vertices: unknown[],
    point: [number, number, number],
): number | null {
    const matchedIndex = vertices.findIndex((vertexShape) => {
        const vertexPoint = extractVertexPoint(puppy, vertexShape);
        if (!vertexPoint) {
            return false;
        }
        return arePointsWithinTolerance(vertexPoint, point);
    });
    return matchedIndex >= 0 ? matchedIndex : null;
}

export function clonePlainData<T>(value: T): T {
    if (typeof structuredClone === 'function') {
        try {
            return structuredClone(value);
        } catch {
            // 退回到 JSON 深拷贝，确保 worker 可传输的数据稳定。
        }
    }
    return JSON.parse(JSON.stringify(value)) as T;
}

export function resolveBlendSourceObject(
    state: GeometryState,
    upstreamBodyObjectId: string | undefined,
    items: SelectionItemRef[],
): ResolvedBlendSource {
    const itemSourceObjectIds = Array.from(new Set(
        items
            .map((item) => item.bodyObjectId)
            .filter((value): value is string => typeof value === 'string' && value.length > 0),
    ));
    const preferredItemObjectId = itemSourceObjectIds.length === 1 ? itemSourceObjectIds[0]! : null;
    const candidateObjectIds = [
        preferredItemObjectId,
        upstreamBodyObjectId,
        ...itemSourceObjectIds,
    ].filter((value, index, array): value is string => (
        typeof value === 'string'
        && value.length > 0
        && array.indexOf(value) === index
    ));
    const sourceObjectId = resolveFirstExistingWorkerModelBodyRevisionId(state, candidateObjectIds)
        ?? resolveWorkerModelBodyRevisionId(state, candidateObjectIds[0])
        ?? candidateObjectIds[0];
    if (!sourceObjectId) {
        throw new Error('圆角/倒角缺少上游实体对象 ID');
    }

    let resolvedSourceObjectId = sourceObjectId;
    let sourceObject = state.getObject(resolvedSourceObjectId) as BlendSourceSolidObject | undefined;
    if (!sourceObject) {
        throw new Error(`未找到上游实体对象: ${sourceObjectId}`);
    }
    if (sourceObject.type !== GeometryType.SOLID) {
        throw new Error(`圆角/倒角仅支持实体对象: ${sourceObjectId}`);
    }
    if (!sourceObject.shape) {
        throw new Error(`实体对象缺少 Puppy CAD 内核 Shape: ${sourceObjectId}`);
    }

    return {
        sourceObjectId: resolvedSourceObjectId,
        sourceObject,
    };
}


/**
 * 按与拓扑快照完全一致的约定构建边几何引用。
 *
 * 设计说明：
 * 1. 闭合边（整圆/整椭圆）在 Puppy CAD 内核 中首尾是同一个缝合点，直接用两顶点会得到退化的 `P|P`；
 * 2. 拓扑快照对这种边改用曲线采样点取两个不同参考点，UI 选择项持久化的正是该约定；
 * 3. 求解侧若仍用原始顶点，同一条圆边在两侧会得到不同引用，解析必然失败——
 *    表现为圆边无法创建圆角、而直边一切正常，且失败发生在 Puppy CAD 内核 求解之前；
 * 4. 因此这里必须复用 `resolveEdgeReferencePoints`，保证两侧引用完全同源。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边 Shape。
 * @returns 边几何引用；参考点不足时返回 null。
 */
function buildEdgeGeometryRefFromShape(puppy: PuppyKernel, edgeShape: unknown): string | null {
    const referencePoints = resolveEdgeReferencePoints(
        extractEdgeVertices(puppy, edgeShape),
        sampleEdgeCurveFromPuppyKernel(puppy, edgeShape),
    );
    if (!referencePoints) {
        return null;
    }
    return buildTopologyEdgeRef(referencePoints[0].point, referencePoints[1].point);
}

export function resolveEdgeSelections(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    items: SelectionItemRef[],
    options: ResolveTopologySelectionOptions = {},
): ResolvedTopologySelection[] {
    /**
     * 禁止对原始边列表做去重。
     *
     * 设计说明：
     * 1. TopExp_Explorer 枚举 Solid 时，同一条物理边会因被多个 Face 共享而出现多次；
     * 2. provenance 中记录的 edgeIndex 基于此原始枚举顺序；
     * 3. 如果在这里去重，后续边的 index 会偏移，导致 buildEdgeTopologyCandidates
     *    通过 edgeIndex 回绑 provenance 时拿到错误的身份信息，进而使 matcher 解析到错误的边。
     * 4. 候选去重交由 buildEdgeTopologyCandidates 内部处理，只去重候选不改变 edges 数组。
     */
    const edges = enumerateShapeItems(puppy, shape, 'edge');
    const topologyCandidates = buildEdgeTopologyCandidates(puppy, shape, metadata, edges);
    return items.map((selection) => {
        const edgeIndex = blendEdgeTopologyMatcher.resolve({
            sourceObjectId: options.sourceObjectId,
            selection: {
                subEntityId: selection.subEntityId,
                semanticEdgeName: selection.semanticName ?? null,
                persistentRef: selection.persistentRef,
                adjacentFaceRefs: selection.adjacentFaceRefs,
                runtimeTopologyHint: selection.runtimeTopologyHint?.kind === 'edge'
                    ? {
                        kind: 'edge',
                        sourceObjectId: selection.runtimeTopologyHint.sourceObjectId,
                        topologyIndex: selection.runtimeTopologyHint.topologyIndex,
                        persistentRef: selection.runtimeTopologyHint.persistentRef,
                        subEntityId: selection.runtimeTopologyHint.subEntityId,
                        adjacentFaceRefs: selection.runtimeTopologyHint.adjacentFaceRefs,
                        adjacentFaceHints: selection.runtimeTopologyHint.adjacentFaceHints,
                        shapeHash: selection.runtimeTopologyHint.shapeHash,
                    }
                    : null,
            },
            candidates: topologyCandidates,
        });
        const edgeShape = edges[edgeIndex];
        if (!edgeShape) {
            throw new Error(`边索引越界，无法解析圆角/倒角边: ${selection.persistentRef}`);
        }
        /**
         * 本次求解内的语义名补全：老命令只存坐标引用，坐标解析成功的这一次把
         * 当前重建产出的语义名补进选择项，供下游求解环节共享同一份身份。
         * 已有语义名的选择项不覆盖，避免用弱身份污染权威身份。
         *
         * 注意：这里只影响 worker 内的本次求解，**不构成持久化自愈**。
         * 命令落库的自愈发生在主线程 `FeatureRebuildOrchestrator` —— 它用 live 快照
         * 重解析绑定并回写 `definition` 与 `referenceBindings`。
         */
        const healedSemanticName = topologyCandidates
            .find((candidate) => candidate.edgeIndex === edgeIndex)?.semanticEdgeName ?? null;
        const healedSelection = !selection.semanticName && healedSemanticName
            ? { ...selection, semanticName: healedSemanticName }
            : selection;
        return {
            edgeShape,
            selection: healedSelection,
        };
    });
}

export function resolveFaceShapeByRef(
    puppy: PuppyKernel,
    shape: unknown,
    metadata: Record<string, unknown>,
    persistentRef: string,
): unknown {
    const faces = enumerateShapeItems(puppy, shape, 'face');
    const faceIndex = resolveFaceIndexByPersistentRef(puppy, faces, metadata, persistentRef);
    const faceShape = faces[faceIndex];
    if (!faceShape) {
        throw new Error(`面索引越界，无法解析面引用: ${persistentRef}`);
    }
    return faceShape;
}

function resolveVertexShapeByRef(
    puppy: PuppyKernel,
    shape: unknown,
    persistentRef: string,
): unknown {
    const vertices = enumerateShapeItems(puppy, shape, 'vertex');
    const vertexIndex = resolveVertexIndexByPersistentRef(puppy, shape, persistentRef);
    const vertexShape = vertices[vertexIndex];
    if (!vertexShape) {
        throw new Error(`顶点索引越界，无法解析顶点引用: ${persistentRef}`);
    }
    return vertexShape;
}

export interface ResolvedVertexCandidateMatch {
    vertexShape: unknown;
    topologyIndex: number;
    matchesRuntimeTopologyHint: boolean;
    matchesPersistentRef: boolean;
    matchesPoint: boolean;
}

function collectUniqueVertexShapes(
    puppy: PuppyKernel,
    shape: unknown,
): unknown[] {
    const rawVertices = enumerateShapeItems(puppy, shape, 'vertex');
    return dedupeEquivalentShapes(rawVertices);
}

function doesVertexMatchPersistentRef(
    puppy: PuppyKernel,
    vertexShape: unknown,
    persistentRef?: string,
): boolean {
    if (!persistentRef) {
        return false;
    }
    const point = extractVertexPoint(puppy, vertexShape);
    if (!point) {
        return false;
    }
    return buildTopologyVertexRef(point) === persistentRef;
}

function doesVertexMatchPoint(
    puppy: PuppyKernel,
    vertexShape: unknown,
    point?: [number, number, number],
): boolean {
    if (!Array.isArray(point) || point.length !== 3) {
        return false;
    }
    const vertexPoint = extractVertexPoint(puppy, vertexShape);
    if (!vertexPoint) {
        return false;
    }
    return arePointsWithinTolerance(vertexPoint, point);
}

function rankVertexCandidate(candidate: ResolvedVertexCandidateMatch): number {
    return (candidate.matchesPersistentRef ? 4 : 0)
        + (candidate.matchesPoint ? 2 : 0)
        + (candidate.matchesRuntimeTopologyHint ? 1 : 0);
}

/**
 * 顶点参数的持久化引用可能同时携带 hint / persistentRef / point 三条线索。
 * 这里先把所有候选顶点收集出来，后续由调用方按具体语义继续裁决。
 */
export function resolveVertexCandidatesByPointRef(
    puppy: PuppyKernel,
    shape: unknown,
    pointRef: BlendPointRef,
): ResolvedVertexCandidateMatch[] {
    const vertices = collectUniqueVertexShapes(puppy, shape);
    const hintedIndex = resolveVertexIndexByRuntimeTopologyHint(pointRef, vertices);

    return vertices.reduce<ResolvedVertexCandidateMatch[]>((result, vertexShape, topologyIndex) => {
        const matchesRuntimeTopologyHint = hintedIndex === topologyIndex;
        const matchesPersistentRef = doesVertexMatchPersistentRef(puppy, vertexShape, pointRef.persistentRef);
        const matchesPoint = doesVertexMatchPoint(puppy, vertexShape, pointRef.point);
        if (!matchesRuntimeTopologyHint && !matchesPersistentRef && !matchesPoint) {
            return result;
        }
        return [
            ...result,
            {
                vertexShape,
                topologyIndex,
                matchesRuntimeTopologyHint,
                matchesPersistentRef,
                matchesPoint,
            },
        ];
    }, []);
}

/**
 * Help Point 这类顶点参数必须优先使用运行时权威顶点 hint 回绑 Puppy CAD 内核 顶点，
 * 否则"选面后再选公共顶点"的真实链路会因为坐标量化漂移，把合法顶点误判成找不到。
 */
export function resolveVertexShapeByPointRef(
    puppy: PuppyKernel,
    shape: unknown,
    pointRef: BlendPointRef,
): unknown {
    const bestCandidate = [...resolveVertexCandidatesByPointRef(puppy, shape, pointRef)]
        .sort((left, right) => (
            rankVertexCandidate(right) - rankVertexCandidate(left)
            || left.topologyIndex - right.topologyIndex
        ))[0];
    if (bestCandidate) {
        return bestCandidate.vertexShape;
    }

    /**
     * 必须对 vertex 枚举结果做拓扑去重，使索引与权威快照（topology-snapshot.ts 中 dedupe: true）一致。
     * TopExp_Explorer 遍历 solid 时，同一个 Puppy CAD 内核 顶点会被每条关联边各枚举一次，
     * 导致未去重列表长度远大于唯一顶点数；而 runtimeTopologyHint.topologyIndex
     * 来自去重后的权威快照索引，直接用于未去重列表会取到错误的顶点。
     */
    const rawVertices = enumerateShapeItems(puppy, shape, 'vertex');
    const vertices = rawVertices.filter((vertex, index) =>
        rawVertices.findIndex((candidate) => areShapesEquivalent(candidate, vertex)) === index,
    );

    const hintedIndex = resolveVertexIndexByRuntimeTopologyHint(pointRef, vertices);
    if (typeof hintedIndex === 'number') {
        const hintedVertex = vertices[hintedIndex];
        if (hintedVertex) {
            /**
             * 防御性校验：runtimeTopologyHint.topologyIndex 来自权威快照的去重顺序，
             * 而当前枚举去重后的顶点顺序可能因 shape 对象差异而不一致。
             * 如果通过 index 取到的顶点坐标与 pointRef.point 不匹配，
             * 说明去重顺序偏移了，必须回退到坐标匹配，不能返回错误的顶点。
             */
            if (Array.isArray(pointRef.point) && pointRef.point.length === 3) {
                const hintedPoint = extractVertexPoint(puppy, hintedVertex);
                if (hintedPoint && arePointsWithinTolerance(hintedPoint, pointRef.point)) {
                    return hintedVertex;
                }
                // index 取到的顶点坐标不匹配 → 去重顺序与快照不一致，回退到后续匹配链路
            } else {
                return hintedVertex;
            }
        }
    }

    if (pointRef.persistentRef) {
        try {
            return resolveVertexShapeByRef(puppy, shape, pointRef.persistentRef);
        } catch {
            // 旧项目数据仍可能只有 persistentRef，这里继续回退到点位匹配。
        }
    }

    const pointMatchedIndex = resolveVertexIndexByPoint(puppy, vertices, pointRef.point);
    if (typeof pointMatchedIndex === 'number') {
        const pointMatchedVertex = vertices[pointMatchedIndex];
        if (pointMatchedVertex) {
            return pointMatchedVertex;
        }
    }

    if (pointRef.persistentRef) {
        throw new Error(`未找到顶点拓扑引用: ${pointRef.persistentRef}`);
    }
    throw new Error(`未找到顶点拓扑引用: ${pointRef.bodyObjectId}`);
}

export function resolveIncidentEdgeShapesByVertex(
    puppy: PuppyKernel,
    shape: unknown,
    vertexShape: unknown,
): unknown[] {
    const vertexPoint = extractVertexPoint(puppy, vertexShape);
    const edges = enumerateShapeItems(puppy, shape, 'edge');
    const incidentEdges = edges.filter((edgeShape) => {
        const vertices = enumerateShapeItems(puppy, edgeShape, 'vertex');
        // 优先精确 IsSame 匹配
        if (vertices.some((candidate) => areShapesEquivalent(candidate, vertexShape))) {
            return true;
        }
        // 回退到坐标匹配，处理 shape 对象差异导致的 IsSame 断裂
        if (vertexPoint) {
            return vertices.some((candidate) => {
                const candidatePoint = extractVertexPoint(puppy, candidate);
                return candidatePoint !== null && arePointsWithinTolerance(candidatePoint, vertexPoint);
            });
        }
        return false;
    });
    return dedupeEdgeShapesByGeometry(puppy, incidentEdges);
}

export function resolveFaceShapesByVertex(
    puppy: PuppyKernel,
    shape: unknown,
    vertexShape: unknown,
): unknown[] {
    const vertexPoint = extractVertexPoint(puppy, vertexShape);
    const faces = enumerateShapeItems(puppy, shape, 'face');
    return faces.filter((faceShape) => {
        const vertices = enumerateShapeItems(puppy, faceShape, 'vertex');
        // 优先精确 IsSame 匹配
        if (vertices.some((candidate) => areShapesEquivalent(candidate, vertexShape))) {
            return true;
        }
        // 回退到坐标匹配，处理 shape 对象差异导致的 IsSame 断裂
        if (vertexPoint) {
            return vertices.some((candidate) => {
                const candidatePoint = extractVertexPoint(puppy, candidate);
                return candidatePoint !== null && arePointsWithinTolerance(candidatePoint, vertexPoint);
            });
        }
        return false;
    });
}

export function buildFallbackFaceRef(faceIndex: number): string {
    return buildTopologyFaceRef(faceIndex);
}
